import React, { useEffect, useLayoutEffect, useRef, useState } from 'react';
import { Link } from 'react-router-dom';
import gsap from 'gsap';
import {
  ArrowLeft,
  ArrowRight,
  CalendarClock,
  CalendarDays,
  CalendarRange,
  Check,
  CircleHelp,
  CreditCard,
  Globe,
  Hourglass,
  Layers,
  LayoutDashboard,
  Lightbulb,
  LoaderCircle,
  MessageCircle,
  PenTool,
  RotateCcw,
  Smartphone,
  TrendingUp,
  UserRound,
  Workflow,
  Zap,
} from 'lucide-react';
import { RING_PATH } from './LogoMark';
import { prefersReduced } from './motion';
import { sendLead } from '../../../lib/sendLead';

/* ------------------------------------------------------------------ */
/* Conteúdo                                                            */
/* ------------------------------------------------------------------ */

const BUDGETS = ['Até R$ 15 mil', 'R$ 15 a 40 mil', 'R$ 40 a 80 mil', 'R$ 80 a 150 mil', 'Acima de R$ 150 mil'];

const STEPS = [
  {
    key: 'type',
    kind: 'single',
    q: 'O que você quer construir?',
    options: [
      { v: 'App mobile', hint: 'iOS e Android', Icon: Smartphone },
      { v: 'Plataforma web', hint: 'Sistema, portal ou SaaS', Icon: Globe },
      { v: 'App e plataforma web', hint: 'O produto completo', Icon: Layers },
      { v: 'Ainda não sei', hint: 'Descobrimos juntos', Icon: CircleHelp },
    ],
  },
  {
    key: 'phase',
    kind: 'single',
    q: 'Em que ponto está a sua ideia?',
    options: [
      { v: 'Só a ideia', hint: 'Começar do zero', Icon: Lightbulb },
      { v: 'Tenho protótipo ou design', hint: 'Já tem uma cara', Icon: PenTool },
      { v: 'Já existe e quero evoluir', hint: 'Novas funções', Icon: TrendingUp },
      { v: 'Preciso refazer', hint: 'Recomeçar melhor', Icon: RotateCcw },
    ],
  },
  {
    key: 'features',
    kind: 'multi',
    q: 'O que o produto precisa ter?',
    sub: 'Escolha quantos quiser.',
    options: [
      { v: 'Login e contas', Icon: UserRound },
      { v: 'Pagamentos e assinaturas', Icon: CreditCard },
      { v: 'Agendamentos', Icon: CalendarDays },
      { v: 'Chat e notificações', Icon: MessageCircle },
      { v: 'Painel administrativo', Icon: LayoutDashboard },
      { v: 'Integrações', Icon: Workflow },
    ],
  },
  {
    key: 'deadline',
    kind: 'single',
    q: 'Para quando você precisa?',
    options: [
      { v: 'O quanto antes', Icon: Zap },
      { v: 'Em 1 a 3 meses', Icon: CalendarClock },
      { v: 'Em 3 a 6 meses', Icon: CalendarRange },
      { v: 'Sem pressa', Icon: Hourglass },
    ],
  },
  { key: 'budget', kind: 'range', q: 'Quanto pretende investir?' },
  { key: 'idea', kind: 'text', q: 'Conte a ideia em poucas palavras.' },
  { key: 'contact', kind: 'contact', q: 'Para quem enviamos a proposta?' },
];

const INITIAL = {
  type: '',
  phase: '',
  features: [],
  deadline: '',
  budget: 1,
  budgetSet: false,
  budgetSkip: false,
  idea: '',
  name: '',
  email: '',
  phone: '',
  company: '',
  website: '', // honeypot anti-spam (invisível para pessoas)
};

/* ------------------------------------------------------------------ */
/* Validação e máscara                                                 */
/* ------------------------------------------------------------------ */

const digits = (v) => v.replace(/\D/g, '');
const nameOk = (v) => v.trim().length >= 2;
const emailOk = (v) => /^[^\s@]+@[^\s@]+\.[^\s@]{2,}$/.test(v.trim());
const phoneOk = (v) => {
  const d = digits(v);
  return d.length === 10 || d.length === 11;
};
const maskPhone = (v) => {
  const d = digits(v).slice(0, 11);
  if (!d) return '';
  if (d.length <= 2) return `(${d}`;
  if (d.length <= 6) return `(${d.slice(0, 2)}) ${d.slice(2)}`;
  if (d.length <= 10) return `(${d.slice(0, 2)}) ${d.slice(2, 6)}-${d.slice(6)}`;
  return `(${d.slice(0, 2)}) ${d.slice(2, 7)}-${d.slice(7)}`;
};

// Cada item respondido é uma "casa" da roda: 9 no total.
const answeredUnits = (a, step) => [
  !!a.type,
  !!a.phase,
  a.features.length > 0,
  !!a.deadline,
  a.budgetSet || a.budgetSkip || step > 4,
  a.idea.trim().length >= 12 || step > 5,
  nameOk(a.name),
  emailOk(a.email),
  phoneOk(a.phone),
];
const UNITS = answeredUnits(INITIAL, 0).length;

/* ------------------------------------------------------------------ */
/* Peças                                                               */
/* ------------------------------------------------------------------ */

// O preenchimento do chip nasce exatamente do ponto clicado (círculo em clip-path).
function setOrigin(e) {
  const el = e.currentTarget;
  const r = el.getBoundingClientRect();
  const fromPointer = e.detail > 0;
  el.style.setProperty('--x', `${fromPointer ? e.clientX - r.left : r.width / 2}px`);
  el.style.setProperty('--y', `${fromPointer ? e.clientY - r.top : r.height / 2}px`);
}

function Chip({ on, onPick, option, multi }) {
  const { v, hint, Icon } = option;
  return (
    <button
      type="button"
      className={`pw-chip pw-anim ${on ? 'is-on' : ''}`}
      aria-pressed={on}
      onClick={(e) => {
        setOrigin(e);
        onPick(v);
      }}
    >
      <span className="pw-chip-fill" aria-hidden />
      <span className="pw-chip-icon" aria-hidden>
        <Icon size={20} strokeWidth={1.6} />
      </span>
      <span className="pw-chip-text">
        <span className="pw-chip-label">{v}</span>
        {hint && <span className="pw-chip-hint">{hint}</span>}
      </span>
      {multi && (
        <span className="pw-chip-check" aria-hidden>
          <Check size={13} strokeWidth={3} />
        </span>
      )}
    </button>
  );
}

function Field({ id, label, optional, ok, error, children }) {
  return (
    <div className={`pw-field pw-anim ${error ? 'has-error' : ''}`}>
      <label htmlFor={id}>
        {label}
        {optional && <span className="pw-optional"> (opcional)</span>}
      </label>
      <div className="pw-input-wrap">
        {children}
        <span className={`pw-valid ${ok ? 'is-on' : ''}`} aria-hidden>
          <Check size={14} strokeWidth={3} />
        </span>
      </div>
      {error && (
        <span className="pw-error" id={`${id}-err`}>
          {error}
        </span>
      )}
    </div>
  );
}

/* ------------------------------------------------------------------ */
/* A roda                                                              */
/* ------------------------------------------------------------------ */

function ProjectWheel() {
  const [a, setA] = useState(INITIAL);
  const [step, setStep] = useState(0);
  const [dir, setDir] = useState(1);
  const [status, setStatus] = useState('idle'); // idle | sending | done | error
  const [touched, setTouched] = useState(false);
  const [ticks, setTicks] = useState([]);

  const root = useRef(null);
  const bodyRef = useRef(null);
  const titleRef = useRef(null);
  const prog = useRef({ p: 0 });
  const last = useRef(0);
  const advance = useRef(0);
  const userMoved = useRef(false);

  const done = status === 'done';
  const units = answeredUnits(a, step);
  const progress = done ? 1 : units.filter(Boolean).length / UNITS;
  const current = STEPS[step];

  // Posição das 9 marcas ao longo do anel (uma por item respondido).
  useLayoutEffect(() => {
    const path = root.current.querySelector('.pw-track');
    const len = path.getTotalLength();
    setTicks(
      Array.from({ length: UNITS }, (_, i) => {
        const pt = path.getPointAtLength((len * (i + 1)) / UNITS - 0.01);
        return { x: pt.x, y: pt.y };
      })
    );
  }, []);

  // Progresso: o traço verde avança com uma faísca na ponta; cada avanço dá um pulso de luz.
  useEffect(() => {
    const el = root.current;
    const path = el.querySelector('.pw-progress');
    const spark = el.querySelector('.pw-spark');
    const pct = el.querySelector('.pw-pct-num');
    const tickEls = el.querySelectorAll('.pw-tick');
    const len = path.getTotalLength();
    path.style.strokeDasharray = `${len}`;

    const render = () => {
      const p = prog.current.p;
      path.style.strokeDashoffset = `${len * (1 - p)}`;
      const pt = path.getPointAtLength(Math.max(0.01, len * p));
      spark.setAttribute('cx', pt.x);
      spark.setAttribute('cy', pt.y);
      spark.style.opacity = p > 0.004 && p < 0.996 ? '1' : '0';
      pct.textContent = String(Math.round(p * 100));
      tickEls.forEach((t, i) => t.classList.toggle('is-on', p >= (i + 1) / UNITS - 0.002));
    };

    const grew = progress > last.current;
    last.current = progress;

    if (prefersReduced()) {
      prog.current.p = progress;
      render();
      return undefined;
    }
    const tween = gsap.to(prog.current, { p: progress, duration: 1.15, ease: 'expo.out', onUpdate: render });
    if (grew) {
      gsap.fromTo(el.querySelector('.pw-pulse'), { opacity: 0.9 }, { opacity: 0, duration: 1.1, ease: 'power2.out' });
      gsap.fromTo(el.querySelector('.pw-pct'), { scale: 1.12 }, { scale: 1, duration: 0.6, ease: 'expo.out' });
    }
    return () => tween.kill();
  }, [progress]);

  // Entrada de cada etapa: sobe com blur e os itens entram em cascata.
  useLayoutEffect(() => {
    const el = bodyRef.current;
    if (!el) return undefined;
    if (userMoved.current && titleRef.current) titleRef.current.focus({ preventScroll: true });
    if (prefersReduced()) return undefined;
    const ctx = gsap.context(() => {
      gsap.fromTo(el, { y: 26 * dir, autoAlpha: 0, filter: 'blur(8px)' }, { y: 0, autoAlpha: 1, filter: 'blur(0px)', duration: 0.65, ease: 'expo.out' });
      gsap.fromTo('.pw-anim', { y: 14, autoAlpha: 0 }, { y: 0, autoAlpha: 1, duration: 0.55, ease: 'expo.out', stagger: 0.045, delay: 0.06 });
    }, el);
    return () => ctx.revert();
  }, [step, done, dir]);

  // Valor do investimento "rola" ao mudar.
  useEffect(() => {
    if (!a.budgetSet || prefersReduced()) return;
    const v = root.current.querySelector('.pw-budget-value');
    if (v) gsap.fromTo(v, { yPercent: 35, opacity: 0.2 }, { yPercent: 0, opacity: 1, duration: 0.4, ease: 'expo.out' });
  }, [a.budget, a.budgetSet]);

  useEffect(() => () => window.clearTimeout(advance.current), []);

  const go = (next) => {
    window.clearTimeout(advance.current);
    if (next === step || next < 0 || next >= STEPS.length) return;
    userMoved.current = true;
    const d = next > step ? 1 : -1;
    const el = bodyRef.current;
    if (prefersReduced() || !el) {
      setDir(d);
      setStep(next);
      return;
    }
    // Saída rápida (Emil: sair mais rápido do que entrar).
    gsap.to(el, {
      y: -20 * d,
      autoAlpha: 0,
      filter: 'blur(6px)',
      duration: 0.2,
      ease: 'power2.in',
      onComplete: () => {
        setDir(d);
        setStep(next);
      },
    });
  };

  const pickSingle = (key) => (v) => {
    setA((s) => ({ ...s, [key]: v }));
    window.clearTimeout(advance.current);
    // Avança sozinho: a pessoa vê a escolha acender e a roda crescer antes de seguir.
    advance.current = window.setTimeout(() => go(step + 1), 480);
  };

  const toggleFeature = (v) =>
    setA((s) => ({
      ...s,
      features: s.features.includes(v) ? s.features.filter((f) => f !== v) : [...s.features, v],
    }));

  const set = (key) => (e) => {
    const value = key === 'phone' ? maskPhone(e.target.value) : e.target.value;
    setA((s) => ({ ...s, [key]: value }));
  };

  const contactValid = nameOk(a.name) && emailOk(a.email) && phoneOk(a.phone);

  const submit = async (e) => {
    e.preventDefault();
    setTouched(true);
    if (!contactValid || status === 'sending') return;
    // Bot preencheu o campo invisível: finge sucesso e não envia.
    if (a.website) {
      setStatus('done');
      return;
    }
    setStatus('sending');
    try {
      await sendLead({
        source: 'wheel',
        type: a.type,
        phase: a.phase,
        features: a.features,
        deadline: a.deadline,
        budget: a.budgetSkip ? 'Prefere conversar' : BUDGETS[a.budget],
        idea: a.idea,
        name: a.name.trim(),
        email: a.email.trim(),
        phone: a.phone,
        company: a.company.trim(),
        page: window.location.pathname,
      });
      userMoved.current = true;
      setStatus('done');
    } catch (err) {
      // eslint-disable-next-line no-console
      console.warn('Envio do projeto falhou:', err);
      setStatus('error');
    }
  };

  const restart = () => {
    userMoved.current = true;
    setA(INITIAL);
    setTouched(false);
    setStatus('idle');
    setDir(-1);
    setStep(0);
  };

  /* ---------------- Conteúdo de cada etapa ---------------- */

  const renderStep = () => {
    if (current.kind === 'single') {
      return (
        <div className="pw-options">
          {current.options.map((o) => (
            <Chip key={o.v} option={o} on={a[current.key] === o.v} onPick={pickSingle(current.key)} />
          ))}
        </div>
      );
    }

    if (current.kind === 'multi') {
      return (
        <>
          <div className="pw-options pw-options--multi">
            {current.options.map((o) => (
              <Chip key={o.v} option={o} multi on={a.features.includes(o.v)} onPick={toggleFeature} />
            ))}
          </div>
          <div className="pw-foot pw-anim">
            <button type="button" className="pw-next" onClick={() => go(step + 1)}>
              {a.features.length ? 'Continuar' : 'Pular'}
              <ArrowRight size={18} strokeWidth={2} aria-hidden />
            </button>
          </div>
        </>
      );
    }

    if (current.kind === 'range') {
      const fill = `${(a.budget / (BUDGETS.length - 1)) * 100}%`;
      return (
        <>
          <div className={`pw-budget pw-anim ${a.budgetSkip ? 'is-skip' : ''}`}>
            <div className="pw-budget-view">
              <output className="pw-budget-value" htmlFor="pw-budget" aria-live="polite">
                {a.budgetSkip ? 'Vamos conversar' : BUDGETS[a.budget]}
              </output>
            </div>
            <input
              id="pw-budget"
              type="range"
              min="0"
              max={BUDGETS.length - 1}
              step="1"
              value={a.budget}
              disabled={a.budgetSkip}
              style={{ '--fill': fill }}
              aria-label="Faixa de investimento"
              aria-valuetext={BUDGETS[a.budget]}
              onChange={(e) => setA((s) => ({ ...s, budget: Number(e.target.value), budgetSet: true }))}
            />
            <div className="pw-budget-ends" aria-hidden>
              <span>{BUDGETS[0]}</span>
              <span>{BUDGETS[BUDGETS.length - 1]}</span>
            </div>
          </div>
          <label className="pw-switch pw-anim">
            <input
              type="checkbox"
              checked={a.budgetSkip}
              onChange={(e) => setA((s) => ({ ...s, budgetSkip: e.target.checked }))}
            />
            <span className="pw-switch-ui" aria-hidden />
            Prefiro conversar sobre isso
          </label>
          <div className="pw-foot pw-anim">
            <button type="button" className="pw-next" onClick={() => go(step + 1)}>
              Continuar
              <ArrowRight size={18} strokeWidth={2} aria-hidden />
            </button>
          </div>
        </>
      );
    }

    if (current.kind === 'text') {
      return (
        <>
          <div className="pw-field pw-anim">
            <label htmlFor="pw-idea" className="hv3-sr">
              Sua ideia
            </label>
            <textarea
              id="pw-idea"
              rows={4}
              maxLength={600}
              value={a.idea}
              placeholder="Ex.: um app para clínicas agendarem consultas e receberem pagamentos."
              onChange={set('idea')}
            />
            <span className="pw-count" aria-hidden>
              {a.idea.length}/600
            </span>
          </div>
          <div className="pw-foot pw-anim">
            <button type="button" className="pw-next" onClick={() => go(step + 1)}>
              {a.idea.trim() ? 'Continuar' : 'Pular'}
              <ArrowRight size={18} strokeWidth={2} aria-hidden />
            </button>
          </div>
        </>
      );
    }

    // Contato
    const err = (ok, msg) => (touched && !ok ? msg : null);
    return (
      <form className="pw-form" onSubmit={submit} noValidate>
        <div className="pw-grid">
          <Field id="pw-name" label="Nome" ok={nameOk(a.name)} error={err(nameOk(a.name), 'Como podemos te chamar?')}>
            <input id="pw-name" type="text" autoComplete="name" value={a.name} onChange={set('name')} aria-invalid={touched && !nameOk(a.name)} />
          </Field>
          <Field id="pw-phone" label="WhatsApp" ok={phoneOk(a.phone)} error={err(phoneOk(a.phone), 'Informe DDD e número.')}>
            <input id="pw-phone" type="tel" inputMode="tel" autoComplete="tel-national" placeholder="(11) 91234-5678" value={a.phone} onChange={set('phone')} aria-invalid={touched && !phoneOk(a.phone)} />
          </Field>
          <Field id="pw-email" label="E-mail" ok={emailOk(a.email)} error={err(emailOk(a.email), 'Confira o e-mail.')}>
            <input id="pw-email" type="email" autoComplete="email" value={a.email} onChange={set('email')} aria-invalid={touched && !emailOk(a.email)} />
          </Field>
          <Field id="pw-company" label="Empresa" optional ok={a.company.trim().length > 1}>
            <input id="pw-company" type="text" autoComplete="organization" value={a.company} onChange={set('company')} />
          </Field>
        </div>

        {/* Honeypot: fora da tela e fora da navegação por teclado */}
        <input className="pw-hp" type="text" name="website" tabIndex={-1} autoComplete="off" value={a.website} onChange={set('website')} aria-hidden />

        {status === 'error' && (
          <p className="pw-alert pw-anim" role="alert">
            Não conseguimos enviar agora. Tente de novo ou escreva para{' '}
            <a href="mailto:contato@idealizer.com.br">contato@idealizer.com.br</a>.
          </p>
        )}

        <div className="pw-foot pw-anim">
          <button type="submit" className="pw-send" disabled={status === 'sending'}>
            {status === 'sending' ? (
              <>
                <LoaderCircle className="pw-spin" size={18} strokeWidth={2.2} aria-hidden />
                Enviando
              </>
            ) : (
              <>
                Enviar projeto
                <ArrowRight size={18} strokeWidth={2} aria-hidden />
              </>
            )}
          </button>
          <p className="pw-legal">
            Usamos seus dados só para responder a este contato.{' '}
            <Link to="/politica-privacidade">Política de privacidade</Link>
          </p>
        </div>
      </form>
    );
  };

  return (
    <div className={`pw ${done ? 'is-done' : ''} ${status === 'sending' ? 'is-sending' : ''}`} ref={root}>
      <div className="pw-ring" aria-hidden>
        <svg viewBox="26 26 162 162" className="pw-svg" focusable="false">
          <defs>
            <filter id="pw-glow" x="-50%" y="-50%" width="200%" height="200%">
              <feGaussianBlur stdDeviation="2.2" result="b" />
              <feMerge>
                <feMergeNode in="b" />
                <feMergeNode in="SourceGraphic" />
              </feMerge>
            </filter>
          </defs>
          <path className="pw-track" d={RING_PATH} />
          <path className="pw-pulse" d={RING_PATH} filter="url(#pw-glow)" />
          <path className="pw-progress" d={RING_PATH} filter="url(#pw-glow)" />
          {ticks.map((t, i) => (
            <circle key={i} className="pw-tick" cx={t.x} cy={t.y} r="1.35" />
          ))}
          <circle className="pw-spark" r="2.6" filter="url(#pw-glow)" />
        </svg>
        <div className="pw-pct">
          <span className="pw-pct-num">0</span>
          <span className="pw-pct-sign">%</span>
        </div>
      </div>

      <div className="pw-body" ref={bodyRef} key={done ? 'done' : step}>
        {done ? (
          <div className="pw-done" role="status">
            <span className="pw-done-icon pw-anim" aria-hidden>
              <Check size={30} strokeWidth={2.5} />
            </span>
            <h3 className="pw-q pw-anim" ref={titleRef} tabIndex={-1}>
              Recebemos sua ideia.
            </h3>
            <p className="pw-sub pw-anim">
              Vamos estudar o que você contou e falar com você pelo WhatsApp ou e-mail.
            </p>
            <button type="button" className="pw-back pw-anim" onClick={restart}>
              <RotateCcw size={15} strokeWidth={2} aria-hidden />
              Enviar outra ideia
            </button>
          </div>
        ) : (
          <>
            <div className="pw-top pw-anim">
              {step > 0 ? (
                <button type="button" className="pw-back" onClick={() => go(step - 1)}>
                  <ArrowLeft size={15} strokeWidth={2} aria-hidden />
                  Voltar
                </button>
              ) : (
                <span />
              )}
              <span className="pw-step">
                Etapa {step + 1} de {STEPS.length}
              </span>
            </div>
            <h3 className="pw-q pw-anim" ref={titleRef} tabIndex={-1}>
              {current.q}
            </h3>
            {current.sub && <p className="pw-sub pw-anim">{current.sub}</p>}
            {renderStep()}
          </>
        )}
      </div>
    </div>
  );
}

export default ProjectWheel;
